import { useState, useRef, useEffect } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalendarIcon, Clock, MapPin, Camera, CreditCard, AlertCircle, CheckCircle2, X, ArrowLeft, Shield } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { GooglePlacesLocation } from "@/components/GooglePlacesLocation";
import { GooglePlacesAddress } from "@/components/GooglePlacesAddress";
import { validateAddressDistance } from "@/utils/distanceUtils";
import { EmailVerificationDialog } from "@/components/EmailVerificationDialog";
import { useNavigate, Link } from "react-router-dom";

const jobCategories = [
  "Plumbing", "Electrical", "Carpentry", "HVAC", "Painting", "Roofing", 
  "Flooring", "Landscaping", "Cleaning", "General Maintenance", 
  "Masonry", "Locksmith", "Appliance Repair", "Pest Control",
  "Plastering", "Dry-lining", "Wallpapering", "Welding", "Scaffolding"
];

// Helper function to get approximate coordinates for major UK locations
const getLocationCoordinates = (location: string): { lat: number; lng: number } | null => {
  const locationLower = location.toLowerCase();
  
  const coordinates = {
    'london': { lat: 51.5074, lng: -0.1278 },
    'manchester': { lat: 53.4808, lng: -2.2426 },
    'birmingham': { lat: 52.4862, lng: -1.8904 },
    'glasgow': { lat: 55.8642, lng: -4.2518 },
    'edinburgh': { lat: 55.9533, lng: -3.1883 },
    'liverpool': { lat: 53.4084, lng: -2.9916 },
    'bristol': { lat: 51.4545, lng: -2.5879 },
    'leeds': { lat: 53.8008, lng: -1.5491 },
    'cardiff': { lat: 51.4816, lng: -3.1791 },
    'belfast': { lat: 54.5973, lng: -5.9301 },
    'sheffield': { lat: 53.3811, lng: -1.4701 },
    'newcastle': { lat: 54.9783, lng: -1.6178 },
    'nottingham': { lat: 52.9548, lng: -1.1581 },
    'brighton': { lat: 50.8225, lng: -0.1372 },
    'oxford': { lat: 51.7520, lng: -1.2577 },
    'cambridge': { lat: 52.2053, lng: 0.1218 }
  };
  
  // Try to find a match in the location string
  for (const [city, coords] of Object.entries(coordinates)) {
    if (locationLower.includes(city)) {
      return coords;
    }
  }
  
  // Default to London if no match found
  return coordinates.london;
};

export const CreateJobPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [preferredDate, setPreferredDate] = useState<Date>();
  const [preferredTime, setPreferredTime] = useState("");
  const [currentStep, setCurrentStep] = useState<'job-details' | 'customer-details'>('job-details');
  const [jobFormData, setJobFormData] = useState<any>(null);
  const { toast } = useToast();

  // Required photo selection before creating the job
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const [locationValue, setLocationValue] = useState("");
  const [addressValue, setAddressValue] = useState("");
  const [locationCoords, setLocationCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [addressCoords, setAddressCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [addressError, setAddressError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const customerFormRef = useRef<HTMLFormElement>(null);

  // Email verification state
  const [showEmailVerify, setShowEmailVerify] = useState(false);
  const [userEmailForVerify, setUserEmailForVerify] = useState("");
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  // Debug effect to track location value changes
  useEffect(() => {
    console.log("🔍 Location value changed to:", locationValue);
  }, [locationValue]);


  const validateUKPhone = (phone: string) => {
    const ukPhoneRegex = /^(?:(?:\+44)|(?:0))(?:(?:7[0-9]{9})|(?:1[0-9]{9})|(?:2[0-9]{9})|(?:3[0-9]{9})|(?:8[0-8][0-9]{8})|(?:9[0-9]{9}))$/;
    return ukPhoneRegex.test(phone.replace(/\s/g, ''));
  };

  const handleSelectPhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const incoming = Array.from(files);
    const total = selectedPhotos.length + incoming.length;
    if (total > 5) {
      toast({ title: "Too many photos", description: "You can upload up to 5 photos.", variant: "destructive" });
    }

    const valid = incoming.filter((f) => {
      if (!f.type.startsWith("image/")) {
        toast({ title: "Invalid file", description: `${f.name} is not an image.`, variant: "destructive" });
        return false;
      }
      if (f.size > 5 * 1024 * 1024) {
        toast({ title: "File too large", description: `${f.name} exceeds 5MB.`, variant: "destructive" });
        return false;
      }
      return true;
    });

    setSelectedPhotos((prev) => [...prev, ...valid].slice(0, 5));
    e.currentTarget.value = "";
  };

  const handleLocationChange = (value: string) => {
    console.log("🏠 Location changed to:", value);
    setLocationValue(value);
    setAddressError("");
  };

  const handleLocationCoordinatesChange = (coords: { lat: number; lng: number } | null) => {
    setLocationCoords(coords);
    setAddressError("");
    
    if (coords && addressCoords) {
      const validation = validateAddressDistance(coords, addressCoords);
      if (!validation.isValid) {
        setAddressError(validation.error || "Address is too far from location");
      }
    }
  };

  const handleAddressChange = (value: string) => {
    setAddressValue(value);
    if (!value.trim()) {
      setAddressError("");
      setAddressCoords(null);
    }
  };

  const handleAddressCoordinatesChange = (coords: { lat: number; lng: number } | null) => {
    setAddressCoords(coords);
    setAddressError("");
    
    if (locationCoords && coords) {
      const validation = validateAddressDistance(locationCoords, coords);
      if (!validation.isValid) {
        setAddressError(validation.error || "Address is too far from location");
        return;
      }
    }
  };

  const removeSelectedPhoto = (index: number) => {
    setSelectedPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleJobDetailsSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (selectedPhotos.length === 0) {
      toast({
        title: "Photos required",
        description: "Please select at least one photo to show the work needed.",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData(e.currentTarget);
    const jobData = {
      title: formData.get("title") as string,
      description: formData.get("description") as string,
      location: locationValue,
      address: addressValue,
      priority: formData.get("priority") as string,
      jobCategory: formData.get("jobCategory") as string,
      budgetMin: formData.get("budgetMin") as string,
      budgetMax: formData.get("budgetMax") as string,
      preferredDate,
      preferredTime,
    };

    // Validation
    if (!jobData.title.trim()) {
      toast({ title: "Title required", description: "Please enter a job title.", variant: "destructive" });
      return;
    }
    if (!jobData.description.trim()) {
      toast({ title: "Description required", description: "Please enter a job description.", variant: "destructive" });
      return;
    }
    if (!locationValue.trim()) {
      toast({ title: "Location required", description: "Please enter a location.", variant: "destructive" });
      return;
    }
    if (addressError) {
      toast({ title: "Address validation error", description: addressError, variant: "destructive" });
      return;
    }
    if (!jobData.jobCategory) {
      toast({ title: "Job category required", description: "Please select a job category.", variant: "destructive" });
      return;
    }

    // Check if user is authenticated
    const { data: user } = await supabase.auth.getUser();
    if (user.user) {
      await createJobDirectly(jobData, user.user);
    } else {
      setJobFormData(jobData);
      setCurrentStep('customer-details');
    }
  };

  const performCustomerSignupAndJob = async (formEl: HTMLFormElement) => {
    console.log('🚀 [CreateJobPage] Begin account+job flow', {
      step: currentStep,
      haveJobFormData: !!jobFormData,
      photoCount: selectedPhotos.length,
    });
    if (!jobFormData) {
      toast({ title: 'Missing job details', description: 'Please complete the job information first.', variant: 'destructive' });
      return;
    }

    setIsLoading(true);

    const formData = new FormData(formEl);
    const fullName = (formData.get("fullName") as string)?.trim();
    const email = (formData.get("email") as string)?.trim();
    const phone = (formData.get("phone") as string)?.trim();
    const password = (formData.get("password") as string) || '';
    const address = addressValue; // controlled state

    console.log('🧾 Collected customer data', { fullName, email, phone, hasPassword: !!password, hasAddress: !!address });

    // Basic validations
    if (!fullName || !email || !phone || !password || !address?.trim()) {
      toast({ title: 'Missing information', description: 'Please complete all required fields.', variant: 'destructive' });
      setIsLoading(false);
      return;
    }
    if (password.length < 6) {
      toast({ title: 'Weak password', description: 'Password must be at least 6 characters.', variant: 'destructive' });
      setIsLoading(false);
      return;
    }
    if (!validateUKPhone(phone)) {
      toast({ title: 'Invalid phone number', description: 'Please enter a valid UK phone number (e.g., 07123456789).', variant: 'destructive' });
      setIsLoading(false);
      return;
    }

    try {
      // 1) Sign up
      console.log('🧭 Step 1/4: Creating account...');
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: { full_name: fullName, user_type: 'customer', phone, address },
        },
      });
      
      // Handle "user already exists" by signing in
      if (authError) {
        if (authError.message === 'User already registered') {
          console.log('⚠️ User exists, signing in instead...');
          const { data: signInData, error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
          
          if (signInErr) {
            console.error('🔴 Sign in failed:', signInErr);
            throw new Error('Account exists but sign in failed. Please check your password.');
          }
          
          if (!signInData.user || !signInData.session) {
            throw new Error('Sign in succeeded but no session returned');
          }
          
          console.log('✅ Signed in existing user:', signInData.user.id);
          
          // Fetch existing profile
          console.log('🧭 Step 2/4: Fetching profile...');
          const { data: existingProfile } = await supabase
            .from('profiles')
            .select('id')
            .eq('user_id', signInData.user.id)
            .maybeSingle();
          
          if (!existingProfile) throw new Error('Profile not found for existing user');
          console.log('✅ Profile ready:', existingProfile.id);
          
          // Create job
          console.log('🧭 Step 3/4: Creating job; Step 4/4: Uploading photos...');
          await createJobDirectly(jobFormData, signInData.user, existingProfile.id);
          console.log('🎉 Flow completed successfully');
          toast({ title: 'Job posted!', description: `Job posted with ${selectedPhotos.length} photo(s).` });
          return;
        }
        
        console.error('🔴 Signup error:', authError);
        throw authError;
      }
      
      if (!authData.user) { 
        throw new Error('Failed to create user account'); 
      }
      
      console.log('✅ Account created:', authData.user.id);
      console.log('📧 Email confirmation required:', authData.user.identities?.length === 0);

      // Ensure we have a session for RLS-protected inserts
      const { data: sessCheck } = await supabase.auth.getSession();
      console.log('🔐 Session check:', sessCheck.session ? 'Active' : 'No session');
      
      if (!sessCheck.session) {
        console.log('ℹ️ No active session after sign up, attempting sign in...');
        const { data: signInData, error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
        
        if (signInErr) {
          console.error('⚠️ Sign-in failed:', signInErr.message);
          throw new Error(`Email confirmation required. Please check ${email} and confirm your email address, then try posting the job again.`);
        }
        
        if (!signInData.session) {
          throw new Error(`Email confirmation required. Please check ${email} and confirm your email address.`);
        }
        
        console.log('✅ Session created after sign in');
      }

      // 2) Wait for profile
      console.log('🧭 Step 2/4: Fetching profile...');
      let profile: { id: string } | null = null;
      for (let i = 0; i < 10; i++) {
        const { data: p, error: pErr } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', authData.user.id)
          .maybeSingle();
        if (pErr) { 
          console.error(`🔴 Profile fetch error (attempt ${i + 1}/10):`, pErr); 
        }
        if (p?.id) { profile = p as any; break; }
        console.log(`⏳ Waiting for profile... (attempt ${i + 1}/10)`);
        await new Promise(r => setTimeout(r, 500));
      }
      if (!profile) {
        console.error('🔴 Profile creation timed out after 10 attempts');
        throw new Error('Profile not found after creation');
      }
      console.log('✅ Profile ready:', profile.id);

      // 3) Create job + 4) Upload photos (handled inside)
      console.log('🧭 Step 3/4: Creating job; Step 4/4: Uploading photos...');
      await createJobDirectly(jobFormData, authData.user, profile.id);

      console.log('🎉 Flow completed successfully');
      toast({ title: 'Account created and job posted!', description: `Your account has been created and job posted with ${selectedPhotos.length} photo(s).` });
    } catch (error: any) {
      console.error('🔴 Flow failed:', error);
      toast({ title: 'Error', description: error.message || String(error), variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomerDetailsSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log('🔵 Form submission started');
    await performCustomerSignupAndJob(e.currentTarget as HTMLFormElement);
  };

  const submitCustomerForm = () => {
    console.log('🟦 Page submit button clicked');
    if (isLoading) return;
    const form = customerFormRef.current;
    if (!form) { console.error('Page form ref missing'); toast({ title: 'Form not ready', description: 'Please try again in a moment.', variant: 'destructive' }); return; }
    // Bypass native validation and handle ourselves
    form.noValidate = true;
    void performCustomerSignupAndJob(form);
  };
  const createJobDirectly = async (jobData: any, user: any, profileId?: string) => {
    setIsLoading(true);
    
    try {
      let profile;
      if (profileId) {
        profile = { id: profileId };
      } else {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("id")
          .eq("user_id", user.id)
          .single();
        profile = profileData;
      }

      if (!profile) throw new Error("Profile not found");

      let requiredDate: string | null = null;
      if (jobData.preferredDate && jobData.preferredTime) {
        const [hours, minutes] = jobData.preferredTime.split(':');
        const combinedDate = new Date(jobData.preferredDate);
        combinedDate.setHours(parseInt(hours), parseInt(minutes));
        requiredDate = combinedDate.toISOString();
      }

      const responseBy = new Date();
      responseBy.setDate(responseBy.getDate() + 7);

      const { data: createdJob, error } = await supabase
        .from("jobs")
        .insert({
          customer_id: profile.id,
          title: jobData.title,
          description: jobData.description,
          location: jobData.location,
          address: jobData.address,
          priority: jobData.priority,
          job_category: jobData.jobCategory,
          budget_min: jobData.budgetMin ? parseFloat(jobData.budgetMin) : null,
          budget_max: jobData.budgetMax ? parseFloat(jobData.budgetMax) : null,
          required_date: requiredDate,
          response_by: responseBy.toISOString(),
        })
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!createdJob) throw new Error("Failed to create job");

      // Upload photos
      let uploadedCount = 0;
      await Promise.all(
        selectedPhotos.map(async (file, idx) => {
          const fileExt = file.name.split('.').pop();
          const storagePath = `${createdJob.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;

          const { data: storageData, error: storageError } = await supabase.storage
            .from('job-photos')
            .upload(storagePath, file);
          if (storageError) throw storageError;

          const { error: dbError } = await supabase
            .from('job_photos')
            .insert({
              job_id: createdJob.id,
              file_path: storageData.path,
              file_name: file.name,
              file_size: file.size,
              uploaded_by: profile.id,
            });
          if (dbError) throw dbError;
          uploadedCount += 1;
        })
      );

      // Notify tradesmen
      try {
        const locationCoords = getLocationCoordinates(jobData.location);
        await supabase.functions.invoke('notify-tradesmen-new-job', {
          body: {
            jobId: createdJob.id,
            jobTitle: createdJob.title,
            jobCategory: createdJob.job_category || '',
            requiredSpecialties: createdJob.required_specialties || [],
            budgetMin: createdJob.budget_min,
            budgetMax: createdJob.budget_max,
            priority: createdJob.priority,
            customerLatitude: locationCoords ? locationCoords.lat : null,
            customerLongitude: locationCoords ? locationCoords.lng : null
          }
        });
      } catch (notificationError) {
        console.error('Error sending job notifications:', notificationError);
      }

      if (!profileId) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("email_verified, email")
          .eq("user_id", user.id)
          .single();

        if (!profile?.email_verified) {
          try {
            await supabase.functions.invoke('verify-contact', {
              body: {
                type: 'email',
                action: 'request',
                email: profile?.email,
              },
            });
            
            setUserEmailForVerify(profile?.email || user.email || '');
            setIsEmailVerified(false);
            
            toast({ 
              title: "Job created successfully!", 
              description: `Your job request has been posted with ${selectedPhotos.length} photo(s). Please verify your email to complete the process.` 
            });
            
            setShowEmailVerify(true);
          } catch (error) {
            console.error('Error sending verification email:', error);
          }
        } else {
          toast({ 
            title: "Job created successfully!", 
            description: `Your job request has been posted with ${selectedPhotos.length} photo(s). Qualified tradesmen have been notified.` 
          });
        }
      }
      // Redirect to dashboard focusing the new job
      navigate(`/?job=${createdJob.id}`);
    } catch (error: any) {
      console.error('🔴 Job creation failed:', error);
      toast({
        title: "Error creating job",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToJobDetails = () => {
    setCurrentStep('job-details');
    setJobFormData(null);
  };

  const handleEmailVerificationUpdate = async () => {
    setShowEmailVerify(false);
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')} 
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
            <Link to="/why-us">
              <Button variant="outline" size="sm" className="shadow-sm hover:shadow-md">
                <Shield className="h-4 w-4 mr-2" />
                Why Choose Us?
              </Button>
            </Link>
          </div>
          <div className="text-center">
            <h1 className="text-4xl font-bold text-foreground mb-2">
              {currentStep === 'job-details' 
                ? 'Create New Job Request' 
                : 'Complete Your Profile'
              }
            </h1>
            <p className="text-lg text-gray-600">
              {currentStep === 'job-details' 
                ? 'Tell us about your job and get matched with qualified tradesmen'
                : 'We need your contact details to create your account'
              }
            </p>
          </div>
        </div>

        {currentStep === 'job-details' ? (
          <form onSubmit={handleJobDetailsSubmit} noValidate className="space-y-6">
            {/* Job Details Card */}
            <Card className="bg-white/80 backdrop-blur-sm shadow-elegant border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl font-bold text-foreground">
                  <AlertCircle className="h-5 w-5 text-primary" />
                  Job Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title" className="text-sm font-semibold text-foreground">Job Title</Label>
                    <Input
                      id="title"
                      name="title"
                      placeholder="e.g., Leaky tap repair, Electrical outlet installation"
                      required
                      className="bg-white/90 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="jobCategory" className="text-sm font-semibold text-foreground">Job Category *</Label>
                    <Select name="jobCategory" required>
                      <SelectTrigger className="bg-white/90 border-gray-200 focus:border-blue-500">
                        <SelectValue placeholder="Select job category" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {jobCategories.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="text-sm font-semibold text-foreground">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    placeholder="Provide details about the work needed..."
                    rows={4}
                    required
                    className="bg-white/90 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Job Settings Card */}
            <Card className="bg-white/80 backdrop-blur-sm shadow-elegant border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl font-bold text-foreground">
                  <Clock className="h-5 w-5 text-primary" />
                  Job Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="priority" className="text-sm font-semibold text-foreground">Priority</Label>
                    <Select name="priority" defaultValue="standard">
                      <SelectTrigger className="bg-white/90 border-gray-200 focus:border-blue-500">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="emergency">Emergency (Same day)</SelectItem>
                        <SelectItem value="urgent">Urgent (Within 2 days)</SelectItem>
                        <SelectItem value="standard">Standard (Within a week)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="budgetMin" className="text-sm font-semibold text-foreground">Min Budget (£)</Label>
                    <Input
                      id="budgetMin"
                      name="budgetMin"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="50"
                      className="bg-white/90 border-gray-200 focus:border-blue-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="budgetMax" className="text-sm font-semibold text-foreground">Max Budget (£)</Label>
                    <Input
                      id="budgetMax"
                      name="budgetMax"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="200"
                      className="bg-white/90 border-gray-200 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="h-4 w-4" />
                    <span className="font-medium">Preferred Arrival Date & Time (Optional)</span>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-foreground">Preferred Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal bg-white/90 border-gray-200 hover:bg-white",
                              !preferredDate && "text-gray-500"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {preferredDate ? format(preferredDate, "PPP") : <span>Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-white" align="start">
                           <Calendar
                             mode="single"
                             selected={preferredDate}
                              onSelect={(date) => {
                                console.log("📅 Date selected:", date, "Location value before:", locationValue);
                                setPreferredDate(date);
                                console.log("📅 Location value after date selection:", locationValue);
                              }}
                             disabled={(date) => date < new Date()}
                             initialFocus
                             className="p-3 pointer-events-auto"
                           />
                        </PopoverContent>
                      </Popover>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="preferredTime" className="text-sm font-semibold text-foreground">Preferred Time</Label>
                      <Input
                        id="preferredTime"
                        type="time"
                        value={preferredTime}
                        onChange={(e) => setPreferredTime(e.target.value)}
                        className="w-full bg-white/90 border-gray-200 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-600">
                    Specify when you'd like the tradesman to arrive. They can accept or propose an alternative time.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Photos Card */}
            <Card className="bg-white/80 backdrop-blur-sm shadow-elegant border-0">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xl font-bold text-foreground">
                    <Camera className="h-5 w-5 text-primary" />
                    Photos
                    <span className="text-red-500">*</span>
                  </div>
                  <span className={`text-sm px-3 py-1 rounded-full ${selectedPhotos.length === 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {selectedPhotos.length}/5 {selectedPhotos.length === 0 ? '(Required)' : ''}
                  </span>
                </CardTitle>
                <CardDescription>
                  Upload at least one photo to show tradesmen the work that needs to be done. This helps them provide accurate quotes.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedPhotos.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {selectedPhotos.map((file, idx) => (
                      <div key={idx} className="relative rounded-lg overflow-hidden bg-white shadow-sm">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Selected job photo ${idx + 1}`}
                          className="w-full h-32 object-cover"
                        />
                        <Button
                          type="button"
                          size="icon"
                          variant="destructive"
                          className="absolute top-2 right-2 h-6 w-6 shadow-lg"
                          aria-label={`Remove photo ${idx + 1}`}
                          onClick={() => removeSelectedPhoto(idx)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleSelectPhotos}
                  />
                  <Button 
                    type="button" 
                    variant={selectedPhotos.length === 0 ? "hero" : "outline"} 
                    onClick={() => fileInputRef.current?.click()}
                    className={cn(
                      "w-full h-12 text-base font-semibold",
                      selectedPhotos.length === 0 ? "shadow-glow" : "bg-white hover:bg-gray-50"
                    )}
                  >
                    <Camera className="mr-2 h-5 w-5" />
                    {selectedPhotos.length === 0 ? "Add Required Photos" : "Add More Photos"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Location Card - At bottom */}
            <Card className="bg-white/80 backdrop-blur-sm shadow-elegant border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl font-bold text-foreground">
                  <MapPin className="h-5 w-5 text-primary" />
                  Job Location
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="location" className="text-sm font-semibold text-foreground">Specific Address</Label>
                  <GooglePlacesLocation
                    id="location"
                    value={locationValue}
                    onChange={handleLocationChange}
                    onCoordinatesChange={handleLocationCoordinatesChange}
                    placeholder="Enter your full address"
                    required
                    className="bg-white/90 border-gray-200 focus:border-blue-500"
                  />
                </div>
                <p className="text-xs text-gray-600">
                  Providing a specific address helps tradesmen find you more easily.
                </p>
              </CardContent>
            </Card>

            <div className="flex gap-4 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate('/')} className="flex-1 h-12 bg-white hover:bg-gray-50">
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isLoading || selectedPhotos.length === 0} 
                variant="hero" 
                className="flex-2 h-12 text-base font-semibold shadow-glow"
              >
                {isLoading ? "Creating..." : selectedPhotos.length === 0 ? "Add Photos First" : "Continue"}
              </Button>
            </div>
          </form>
        ) : (
          <Card className="bg-white/80 backdrop-blur-sm shadow-elegant border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl font-bold text-foreground">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                Create Your Account
              </CardTitle>
              <CardDescription>
                We need your contact details to create your account and connect you with tradesmen.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form ref={customerFormRef} onSubmit={handleCustomerDetailsSubmit} noValidate className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="text-sm font-semibold text-foreground">Full Name</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      type="text"
                      placeholder="Enter your full name"
                      required
                      className="bg-white/90 border-gray-200 focus:border-blue-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-semibold text-foreground">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Enter your email address"
                      required
                      className="bg-white/90 border-gray-200 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-semibold text-foreground">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="07123456789"
                      required
                      className="bg-white/90 border-gray-200 focus:border-blue-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-semibold text-foreground">Password</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="Create a password"
                      required
                      className="bg-white/90 border-gray-200 focus:border-blue-500"
                    />
                  </div>
                </div>

                 <div className="space-y-2">
                   <Label htmlFor="address" className="text-sm font-semibold text-foreground">Your Address</Label>
                   <GooglePlacesAddress
                     id="address"
                     name="address"
                     value={addressValue}
                     onChange={handleAddressChange}
                     onCoordinatesChange={handleAddressCoordinatesChange}
                     placeholder="e.g., 123 High Street, London, SW1A 1AA"
                     className="bg-white/90 border-gray-200 focus:border-blue-500"
                   />
                 </div>

                <div className="flex gap-4 pt-4">
                  <Button type="button" variant="outline" onClick={handleBackToJobDetails} className="flex-1 h-12 bg-white hover:bg-gray-50">
                    Back
                  </Button>
                  <Button 
                    type="button" 
                    onClick={submitCustomerForm}
                    disabled={isLoading} 
                    variant="hero" 
                    className="flex-2 h-12 text-base font-semibold shadow-glow"
                  >
                    {isLoading ? "Creating Account..." : "Create Account & Post Job"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Email Verification Dialog */}
        {showEmailVerify && (
          <EmailVerificationDialog
            isOpen={showEmailVerify}
            userEmail={userEmailForVerify}
            isVerified={isEmailVerified}
            onVerificationUpdate={handleEmailVerificationUpdate}
            onClose={() => setShowEmailVerify(false)}
          />
        )}
      </div>
    </div>
  );
};

export default CreateJobPage;